import { supabase } from "../lib/supabase";
import { APP_HOME } from "../lib/appRoutes";
import { subscriptionToRow } from "../lib/pushNotifications";

/**
 * @param {PushSubscription} subscription
 */
export async function upsertPushSubscription(subscription) {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error("Bitte melde dich an, um Erinnerungen zu aktivieren.");
  }

  const row = subscriptionToRow(subscription);
  if (!row.endpoint || !row.p256dh || !row.auth) {
    throw new Error("Ungültige Push-Anmeldung.");
  }

  const { data: existing, error: findError } = await supabase
    .from("push_subscriptions")
    .select("id")
    .eq("endpoint", row.endpoint)
    .maybeSingle();

  if (findError) throw findError;

  const payload = {
    user_id: user.id,
    endpoint: row.endpoint,
    p256dh: row.p256dh,
    auth: row.auth,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    updated_at: new Date().toISOString(),
  };

  if (existing?.id) {
    const { error } = await supabase
      .from("push_subscriptions")
      .update(payload)
      .eq("id", existing.id);
    if (error) throw error;
    return existing.id;
  }

  const { data, error } = await supabase
    .from("push_subscriptions")
    .insert(payload)
    .select("id")
    .single();
  if (error) throw error;
  return data.id;
}

/**
 * Remove this device's push row (RLS: own subscriptions only).
 * @param {string} endpoint
 */
export async function removePushSubscriptionByEndpoint(endpoint) {
  if (!endpoint) return;
  const { error } = await supabase
    .from("push_subscriptions")
    .delete()
    .eq("endpoint", endpoint);
  if (error) throw error;
}

/**
 * @param {{ fireAt: Date, title: string, body?: string, url?: string }} params
 */
export async function createNudge({ fireAt, title, body, url = APP_HOME }) {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error("Bitte melde dich an.");
  }

  const { data, error } = await supabase
    .from("nudges")
    .insert({
      user_id: user.id,
      fire_at: fireAt.toISOString(),
      title: title.trim(),
      body: body?.trim() || null,
      url,
    })
    .select("id, fire_at, title, body, url")
    .single();

  if (error) throw error;
  return data;
}

export async function cancelNudge(nudgeId) {
  const { error } = await supabase
    .from("nudges")
    .update({ cancelled_at: new Date().toISOString() })
    .eq("id", nudgeId)
    .is("sent_at", null);
  if (error) throw error;
}

export async function fetchPendingNudges() {
  const { data, error } = await supabase
    .from("nudges")
    .select("id, fire_at, title, body, url")
    .is("sent_at", null)
    .is("cancelled_at", null)
    .order("fire_at", { ascending: true })
    .limit(10);
  if (error) throw error;
  return data || [];
}
